import axios from "axios";
import { canonical } from "@/lib/canon";
import { cacheable, cachePut, cacheGet, cacheClear } from "@/lib/offlineCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

const TOKEN_KEY = "bvd_token";
let memToken = null; // secours si localStorage est indisponible (navigation privée, cookies bloqués)
export const getToken = () => {
  if (memToken) return memToken;
  try { return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setToken = (t) => {
  memToken = t || null;
  if (!t) cacheClear();
  try {
    if (t) { localStorage.setItem(TOKEN_KEY, t); sessionStorage.setItem(TOKEN_KEY, t); }
    else { localStorage.removeItem(TOKEN_KEY); sessionStorage.removeItem(TOKEN_KEY); }
  } catch { /* ignore */ }
};

// Attach the bearer token so auth works even when the cross-site cookie is blocked (deployed domain)
api.interceptors.request.use((cfg) => {
  cfg.url = canonical(cfg.url);
  const tok = getToken();
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

// Endpoints where a 401 is a legitimate business response (not an expired session)
const NON_SESSION_401 = ["/auth/", "/config/kobo/test", "/kobo/", "/activer"];

let sessionExpired = false;

api.interceptors.response.use(
  (r) => {
    sessionExpired = false;
    if (r.status === 200 && cacheable(r.config) && r.data && typeof r.data === "object") cachePut(r.config, r.data);
    return r;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // Réseau indisponible (pas de réponse HTTP) → servir la dernière valeur connue si elle existe
    if (!error?.response && error?.config && cacheable(error.config)) {
      const hit = cacheGet(error.config);
      if (hit) {
        window.dispatchEvent(new CustomEvent("bvd:cache-hit", { detail: { at: hit.t } }));
        return Promise.resolve({ data: hit.data, status: 200, statusText: "OK (cache)", headers: { "x-from-cache": "1" }, config: error.config, fromCache: true, cachedAt: hit.t });
      }
    }
    const isSession401 = status === 401 && !NON_SESSION_401.some((u) => url.includes(u));
    if (isSession401) {
      // Session expired/invalid → let AuthContext show a gentle notice and redirect
      // via React Router (no hard reload). Swallow so unhandled page fetches stay quiet.
      if (!sessionExpired) {
        sessionExpired = true;
        window.dispatchEvent(new CustomEvent("bvd:session-expired"));
      }
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

export function apiErr(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default api;
